import React from 'react';
import { Button, Stack, Typography } from '@mui/material';
import { useRecoilState } from 'recoil';
import { dataTrendPeriodAtom, selectedDateAtom } from '../../../../data-access';
import dayjs from 'dayjs';
import { clsx } from 'clsx';
import './DataHeader.scss';
import { CustomDatePickerModal } from './CustomDatePickerModal';
import { useTranslation } from 'react-i18next';
import { formatDataDate } from '../../i18n';

const DataHeader = () => {
  const { t, i18n } = useTranslation();
  const [selectedDate, setSelectedDate] = useRecoilState(selectedDateAtom);
  const [isCustomDateModalOpen, setIsCustomDateModalOpen] = React.useState(false);
  const [dataTrendPeriod, setDataTrendPeriod] = useRecoilState(dataTrendPeriodAtom);

  const handleDateSelect = (days: number, isThisWeek: boolean = false) => {
    if (isThisWeek) {
      const today = dayjs();
      const thisSunday = today.subtract(today.day(), 'day');
      setSelectedDate(thisSunday);
      setDataTrendPeriod('weekly');
    } else if (days === 7) {
      const today = dayjs();
      const lastSunday = today.subtract(today.day() + 7, 'day');
      setSelectedDate(lastSunday);
      setDataTrendPeriod('weekly');
    } else {
      setSelectedDate(dayjs().subtract(days, 'day'));
      setDataTrendPeriod('daily');
    }
  };

  const today = dayjs();
  const yesterday = today.subtract(1, 'day');
  const thisWeekStart = today.subtract(today.day(), 'day');
  const lastWeekStart = today.subtract(today.day() + 7, 'day');

  const isToday = selectedDate.isSame(today, 'day') && dataTrendPeriod === 'daily';
  const isYesterday = selectedDate.isSame(yesterday, 'day') && dataTrendPeriod === 'daily';
  const isThisWeek = selectedDate.isSame(thisWeekStart, 'day') && dataTrendPeriod === 'weekly';
  const isLastWeek = selectedDate.isSame(lastWeekStart, 'day') && dataTrendPeriod === 'weekly';
  const isCustom = !isToday && !isYesterday && !isThisWeek && !isLastWeek;

  const getCustomButtonText = () => {
    if (!isCustom) return t('data.common.custom');
    
    if (dataTrendPeriod === 'weekly') {
      const weekStart = selectedDate.startOf('week');
      const weekEnd = selectedDate.endOf('week');
      return `${formatDataDate(i18n.language, weekStart.toDate(), {
        month: 'short',
        day: 'numeric',
      })} - ${formatDataDate(i18n.language, weekEnd.toDate(), {
        month: 'short',
        day: 'numeric',
      })}`;
    } else {
      return formatDataDate(i18n.language, selectedDate.toDate(), {
        month: 'short',
        day: 'numeric',
      });
    }
  };

  return (
    <Stack gap={1} ml={2} direction="row" alignItems="flex-start">
      <Stack gap={1} direction="row" alignItems="flex-start">
        <Button
          className={clsx('data-header-container__button', {
            'data-header-container__button--selected': isToday,
          })}
          onClick={() => handleDateSelect(0)}
        >
          <Typography variant="h5">{t('data.common.today')}</Typography>
        </Button>
        <Button
          className={clsx('data-header-container__button', {
            'data-header-container__button--selected': isYesterday,
          })}
          onClick={() => handleDateSelect(1)}
        >
          <Typography variant="h5">{t('data.common.yesterday')}</Typography>
        </Button>
        <Button
          className={clsx('data-header-container__button', {
            'data-header-container__button--selected': isThisWeek,
          })}
          onClick={() => handleDateSelect(0, true)}
        >
          <Typography variant="h5">{t('data.common.thisWeek')}</Typography>
        </Button>
        <Button
          className={clsx('data-header-container__button', {
            'data-header-container__button--selected': isLastWeek,
          })}
          onClick={() => handleDateSelect(7)}
        >
          <Typography variant="h5">{t('data.common.lastWeek')}</Typography>
        </Button>
        <Button
          className={clsx('data-header-container__button', {
            'data-header-container__button--selected': isCustom,
          })}
          onClick={() => setIsCustomDateModalOpen(true)}
        >
          <Typography variant="h5">{getCustomButtonText()}</Typography>
        </Button>
      </Stack>
      <CustomDatePickerModal
        isOpen={isCustomDateModalOpen}
        onClose={() => setIsCustomDateModalOpen(false)}
        selectedDate={selectedDate}
      />
    </Stack>
  );
};

export default DataHeader;
